import { useState } from "react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import {
  SegmentedControl,
  SegmentedControlKind,
  SegmentedControlOption,
  SegmentedControlSurface,
} from "@pages/AdminDashboard/ui/SegmentedControl";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

interface ExampleDefinition {
  name: string;
  label: string;
  kind: SegmentedControlKind;
  surface: SegmentedControlSurface;
  options: SegmentedControlOption[];
  initialValue: string;
}

const periodOptions: SegmentedControlOption[] = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

// One example for each use in the design: the Find tabs, the Email / User ID
// toggle on a canvas surface and the Home range, then four options and a
// disabled option (SEG-R18).
const examples: ExampleDefinition[] = [
  {
    name: "Tabs",
    label: "Search type",
    kind: "tabs",
    surface: "white",
    options: [
      { value: "user", label: "User" },
      { value: "case", label: "Case" },
      { value: "order", label: "Order" },
    ],
    initialValue: "user",
  },
  {
    name: "Toggle on canvas",
    label: "Search for",
    kind: "radios",
    surface: "canvas",
    options: [
      { value: "email", label: "Email" },
      { value: "userId", label: "User ID" },
    ],
    initialValue: "email",
  },
  {
    name: "Range",
    label: "Period",
    kind: "radios",
    surface: "white",
    options: periodOptions,
    initialValue: "30",
  },
  {
    name: "Four options",
    label: "Shown",
    kind: "radios",
    surface: "white",
    options: [
      { value: "all", label: "All" },
      { value: "open", label: "Open" },
      { value: "shipped", label: "Shipped" },
      { value: "closed", label: "Closed" },
    ],
    initialValue: "all",
  },
  {
    name: "Disabled option",
    label: "Period with a disabled option",
    kind: "radios",
    surface: "white",
    options: periodOptions.map((option) =>
      option.value === "custom" ? { ...option, isDisabled: true } : option
    ),
    initialValue: "7",
  },
];

const [spaceXs, , spaceMd, , spaceXl] = adminTokens.spacing;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(420px, 100%), 1fr));
  gap: ${spaceMd}px;
  margin-bottom: ${spaceXl}px;
`;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spaceXs}px;
  border: 1px solid ${adminTokens.color.border};
  border-radius: ${adminTokens.radius.row}px;
  padding: ${spaceMd}px;
`;

const Caption = styled.span`
  color: ${adminTokens.color.inkSoft};
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: ${adminTokens.typeScale.meta.fontSize};
`;

const ExampleControl = ({
  name,
  label,
  kind,
  surface,
  options,
  initialValue,
}: ExampleDefinition) => {
  const [value, setValue] = useState(initialValue);

  return (
    <Example aria-label={name}>
      <Caption>{name}</Caption>
      <SegmentedControl
        label={label}
        kind={kind}
        surface={surface}
        options={options}
        value={value}
        onChange={setValue}
      />
    </Example>
  );
};

export const SegmentedControlSection = () => (
  <UiKitSection title="Segmented control">
    <Grid>
      {examples.map((example) => (
        <ExampleControl key={example.name} {...example} />
      ))}
    </Grid>
  </UiKitSection>
);
